/**
 * Courses In Progress Page for trAInedup.ai
 * Lists the learner's active courses with quick links to resume
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Clock, Loader2 } from 'lucide-react';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { learningPlansService, LikedCourse } from '../services/firebase/learningPlans';
import { ROUTES } from '../utils/constants';

/**
 * Format a duration in minutes to a human readable string
 */
const formatDuration = (minutes: number) => {
  if (!minutes) {
    return 'Less than a minute logged';
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0) {
    return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
  }

  return `${mins}m`;
};

/**
 * Courses in progress page component
 */
export const CoursesInProgressPage: React.FC = () => {
  const [courses, setCourses] = useState<LikedCourse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  const loadCourses = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const allCourses = await learningPlansService.getLearningPlanCourses();
      if (!isMountedRef.current) {
        return;
      }

      const activeCourses = allCourses
        .filter(course => {
          const isActive = course.status === 'active';
          const isNotCompleted = (course.progress ?? 0) < 100;
          // A course is started if it has progress > 0 OR it has been accessed (lastAccessedAt is present)
          const hasStarted = (course.progress ?? 0) > 0 || !!course.lastAccessedAt;
          return isActive && isNotCompleted && hasStarted;
        })
        .sort((a, b) => (b.progress ?? 0) - (a.progress ?? 0));

      setCourses(activeCourses);
    } catch (err) {
      if (!isMountedRef.current) {
        return;
      }

      console.error('Error loading courses in progress:', err);
      setError('Failed to load your courses in progress. Please try again.');
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    loadCourses();

    return () => {
      isMountedRef.current = false;
    };
  }, [loadCourses]);

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6">
        <Card className="flex flex-col items-center justify-center py-16 text-center" border={false} shadow="none">
          <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
          <p className="text-neutral-600">Loading your active courses...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <Card className="max-w-2xl mx-auto text-center py-12">
          <h2 className="text-xl font-semibold text-neutral-800 mb-2">Something went wrong</h2>
          <p className="text-neutral-600 mb-6">{error}</p>
          <Button onClick={loadCourses} variant="primary" icon={ArrowRight} iconPosition="right">
            Try again
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-montserrat font-semibold text-neutral-900">Courses in Progress</h1>
        <p className="text-neutral-600">
          Pick up where you left off and continue your learning journey.
        </p>
      </div>

      {courses.length === 0 ? (
        <Card className="text-center py-16">
          <BookOpen className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-neutral-800 mb-2">No active courses yet</h2>
          <p className="text-neutral-600 mb-6">
            Start a new course from your learning plan or explore the catalog to begin learning.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={ROUTES.learningPlan}>
              <Button variant="primary">View Learning Plan</Button>
            </Link>
            <Link to={ROUTES.courses}>
              <Button variant="outline">Browse Courses</Button>
            </Link>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {courses.map(course => {
            const progress = Math.min(100, Math.max(0, Math.round(course.progress ?? 0)));
            const timeSpentLabel = course.timeSpent > 0
              ? `${formatDuration(course.timeSpent)} spent learning`
              : 'No time logged yet';
            const addedDate = course.addedAt.toDate().toLocaleDateString();
            const startedDate = course.startedAt ? course.startedAt.toDate().toLocaleDateString() : null;

            return (
              <Link
                key={course.courseId}
                to={`/courses/${course.courseId}`}
                className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-lg"
              >
                <Card hover className="h-full">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex-1 space-y-4">
                      <div>
                        <h2 className="text-lg font-semibold text-neutral-900">{course.courseTitle}</h2>
                        <p className="text-sm text-neutral-600">
                          Added {addedDate}
                          {startedDate ? ` • Started ${startedDate}` : ''}
                        </p>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-neutral-700">Progress</span>
                          <span className="text-sm font-semibold text-primary">{progress}%</span>
                        </div>
                        <div className="bg-neutral-200 rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <p className="mt-3 flex items-center text-sm text-neutral-600 gap-2">
                          <Clock className="w-4 h-4 text-neutral-400" />
                          {timeSpentLabel}
                        </p>
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      <Button variant="primary" icon={ArrowRight} iconPosition="right">
                        Resume Course
                      </Button>
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CoursesInProgressPage;
